export type Point = { x: number; y: number }
export type Severity = 'low' | 'moderate' | 'high'
export type Priority = 'high' | 'medium' | 'low'

export interface FaceMetrics {
  symmetry: number // 0-100
  midFaceVolume: Severity
  lipFullness: Severity
  jawDefinition: Severity
  nasolabialProminence: Severity
}

export interface Recommendation {
  id: 'cheeks' | 'lips' | 'nasolabial' | 'jawline' | 'symmetry'
  priority: Priority
  /** 0-100 — confidence/strength of recommendation */
  score: number
}

export interface SubScores {
  facialBalance: number
  volumeBalance: number
  profileBalance: number
  expressionHarmony: number
  skinShadow: number
}

export type ZoneStatus = 'good' | 'improve' | 'attention'

export type ZoneId =
  | 'forehead'
  | 'eyes'
  | 'cheeks'
  | 'nose'
  | 'nasolabial'
  | 'lips'
  | 'chin'
  | 'jawline'

export interface FaceZone {
  id: ZoneId
  /** raw normalized center (0-1), un-flipped */
  pos: Point
  status: ZoneStatus
  score: number
}

export interface FaceAnalysis {
  metrics: FaceMetrics
  recommendations: Recommendation[]
  overallScore: number
  subScores: SubScores
  zones: FaceZone[]
}

const statusToScore = (s: ZoneStatus) =>
  s === 'good' ? 88 : s === 'improve' ? 68 : 48

const dist = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y)

/**
 * Analyze 68-point face landmarks (normalized 0-1) and return facial metrics
 * plus prioritized treatment recommendations. Heuristic — for marketing demo
 * only, not medical advice.
 */
export function analyzeFace(landmarks: Point[]): FaceAnalysis {
  if (landmarks.length < 68) {
    // Not enough landmarks — return neutral analysis
    return {
      metrics: {
        symmetry: 90,
        midFaceVolume: 'moderate',
        lipFullness: 'moderate',
        jawDefinition: 'moderate',
        nasolabialProminence: 'moderate',
      },
      recommendations: [
        { id: 'cheeks', priority: 'medium', score: 60 },
        { id: 'lips', priority: 'medium', score: 60 },
        { id: 'nasolabial', priority: 'low', score: 50 },
      ],
      overallScore: 75,
      subScores: {
        facialBalance: 78,
        volumeBalance: 72,
        profileBalance: 75,
        expressionHarmony: 80,
        skinShadow: 70,
      },
      zones: [],
    }
  }

  // ----- Symmetry -----
  // Compare horizontal distance of paired landmarks from face vertical center.
  const centerX = landmarks.reduce((s, p) => s + p.x, 0) / landmarks.length
  const symmetryPairs: [number, number][] = [
    [0, 16],
    [1, 15],
    [2, 14],
    [3, 13],
    [4, 12],
    [5, 11],
    [6, 10],
    [7, 9],
    [36, 45], // outer eye corners
    [39, 42], // inner eye corners
    [31, 35], // nostril edges
    [48, 54], // mouth corners
  ]
  const faceWidth = dist(landmarks[0], landmarks[16]) || 1
  const asymmetryScores = symmetryPairs.map(([l, r]) => {
    const dl = Math.abs(landmarks[l].x - centerX)
    const dr = Math.abs(landmarks[r].x - centerX)
    return Math.abs(dl - dr) / faceWidth
  })
  const avgAsymmetry =
    asymmetryScores.reduce((s, v) => s + v, 0) / asymmetryScores.length
  const symmetry = Math.max(0, Math.min(100, 100 - avgAsymmetry * 400))

  // ----- Mid-face volume -----
  // Cheek bone width (point 2 to 14) vs face height (chin 8 to brow midpoint 27)
  const cheekWidth = dist(landmarks[2], landmarks[14])
  const faceHeight = dist(landmarks[8], landmarks[27]) || 1
  const midFaceRatio = cheekWidth / faceHeight
  const midFaceVolume: Severity =
    midFaceRatio < 1.55 ? 'low' : midFaceRatio < 1.75 ? 'moderate' : 'high'

  // ----- Lip fullness -----
  // Mouth opening height (upper lip top 51 to lower lip bottom 57) vs width (48 to 54)
  const lipHeight = dist(landmarks[51], landmarks[57])
  const mouthWidth = dist(landmarks[48], landmarks[54]) || 1
  const lipRatio = lipHeight / mouthWidth
  const lipFullness: Severity =
    lipRatio < 0.28 ? 'low' : lipRatio < 0.4 ? 'moderate' : 'high'

  // ----- Jaw definition -----
  // Angle at chin (point 8) formed by jaw points 4 -> 8 -> 12.
  // Sharper angle = more defined jawline.
  const v1 = {
    x: landmarks[4].x - landmarks[8].x,
    y: landmarks[4].y - landmarks[8].y,
  }
  const v2 = {
    x: landmarks[12].x - landmarks[8].x,
    y: landmarks[12].y - landmarks[8].y,
  }
  const dot = v1.x * v2.x + v1.y * v2.y
  const m1 = Math.hypot(v1.x, v1.y)
  const m2 = Math.hypot(v2.x, v2.y)
  const cos = m1 && m2 ? dot / (m1 * m2) : 0
  const jawAngleDeg =
    (Math.acos(Math.max(-1, Math.min(1, cos))) * 180) / Math.PI
  // Wide angle => round/soft jaw; narrow => sharp jaw
  const jawDefinition: Severity =
    jawAngleDeg > 130 ? 'low' : jawAngleDeg > 110 ? 'moderate' : 'high'

  // ----- Nasolabial prominence -----
  // Distance from nose-corner to mouth-corner, normalized by face width.
  const nlLeft = dist(landmarks[31], landmarks[48])
  const nlRight = dist(landmarks[35], landmarks[54])
  const nlRatio = (nlLeft + nlRight) / 2 / faceWidth
  const nasolabialProminence: Severity =
    nlRatio < 0.22 ? 'low' : nlRatio < 0.28 ? 'moderate' : 'high'

  const metrics: FaceMetrics = {
    symmetry: Math.round(symmetry),
    midFaceVolume,
    lipFullness,
    jawDefinition,
    nasolabialProminence,
  }

  // ----- Build recommendations -----
  const recs: Recommendation[] = []

  if (midFaceVolume === 'low') {
    recs.push({ id: 'cheeks', priority: 'high', score: 88 })
  } else if (midFaceVolume === 'moderate') {
    recs.push({ id: 'cheeks', priority: 'medium', score: 65 })
  }

  if (lipFullness === 'low') {
    recs.push({ id: 'lips', priority: 'high', score: 85 })
  } else if (lipFullness === 'moderate') {
    recs.push({ id: 'lips', priority: 'medium', score: 60 })
  }

  if (nasolabialProminence === 'high') {
    recs.push({ id: 'nasolabial', priority: 'high', score: 90 })
  } else if (nasolabialProminence === 'moderate') {
    recs.push({ id: 'nasolabial', priority: 'medium', score: 65 })
  }

  if (jawDefinition === 'low') {
    recs.push({ id: 'jawline', priority: 'high', score: 82 })
  } else if (jawDefinition === 'moderate') {
    recs.push({ id: 'jawline', priority: 'low', score: 55 })
  }

  if (symmetry < 85) {
    recs.push({ id: 'symmetry', priority: 'medium', score: 70 })
  }

  // Sort: priority desc, then score desc
  const order: Record<Priority, number> = { high: 3, medium: 2, low: 1 }
  recs.sort(
    (a, b) => order[b.priority] - order[a.priority] || b.score - a.score,
  )

  // Always return at least 3
  while (recs.length < 3) {
    if (!recs.find((r) => r.id === 'cheeks'))
      recs.push({ id: 'cheeks', priority: 'low', score: 50 })
    else if (!recs.find((r) => r.id === 'lips'))
      recs.push({ id: 'lips', priority: 'low', score: 50 })
    else if (!recs.find((r) => r.id === 'jawline'))
      recs.push({ id: 'jawline', priority: 'low', score: 50 })
    else break
  }

  // ----- Composite scoring -----
  const sevToScore = (
    s: Severity,
    good: 'high' | 'moderate' | 'low' = 'high',
  ) => {
    if (good === 'high') return s === 'high' ? 85 : s === 'moderate' ? 70 : 55
    if (good === 'low') return s === 'low' ? 85 : s === 'moderate' ? 70 : 55
    return s === 'moderate' ? 85 : 65
  }

  const facialBalance = Math.round(
    symmetry * 0.7 + sevToScore(midFaceVolume) * 0.3,
  )
  const volumeBalance = Math.round(
    (sevToScore(midFaceVolume) + sevToScore(lipFullness)) / 2,
  )
  const profileBalance = Math.round(
    (sevToScore(jawDefinition) + sevToScore(nasolabialProminence, 'low')) / 2,
  )
  const expressionHarmony = Math.round(
    (sevToScore(lipFullness, 'moderate') + symmetry * 0.5) / 1.5,
  )
  const skinShadow = Math.round(70 + Math.random() * 15) // Heuristic placeholder

  const subScores: SubScores = {
    facialBalance,
    volumeBalance,
    profileBalance,
    expressionHarmony,
    skinShadow,
  }

  const overallScore = Math.round(
    (facialBalance +
      volumeBalance +
      profileBalance +
      expressionHarmony +
      skinShadow) /
      5,
  )

  // ----- Face zones (for detailed zone-by-zone view) -----
  const flipNot = (p: Point) => p // stored raw; component flips for display
  const cheekCenter = avg([
    landmarks[2],
    landmarks[14],
    landmarks[31],
    landmarks[35],
  ])
  const eyeCenter = avg(landmarks.slice(36, 48))
  const lipCenter = avg(landmarks.slice(48, 68))
  const noseTip = landmarks[30]
  const nlCenter = avg([
    landmarks[31],
    landmarks[48],
    landmarks[35],
    landmarks[54],
  ])
  const chin = landmarks[8]
  const jaw = avg([landmarks[5], landmarks[11]])
  const forehead = {
    x: landmarks[27].x,
    y: Math.max(0, landmarks[27].y - 0.14),
  }

  const cheekStatus: ZoneStatus =
    midFaceVolume === 'low'
      ? 'attention'
      : midFaceVolume === 'moderate'
        ? 'improve'
        : 'good'
  const lipStatus: ZoneStatus = lipFullness === 'low' ? 'improve' : 'good'
  const nlStatus: ZoneStatus =
    nasolabialProminence === 'high'
      ? 'attention'
      : nasolabialProminence === 'moderate'
        ? 'improve'
        : 'good'
  const jawStatus: ZoneStatus =
    jawDefinition === 'low'
      ? 'improve'
      : jawDefinition === 'moderate'
        ? 'good'
        : 'good'
  const symmetryStatus: ZoneStatus =
    symmetry < 82 ? 'attention' : symmetry < 90 ? 'improve' : 'good'

  const zones: FaceZone[] = [
    { id: 'forehead', pos: flipNot(forehead), status: 'good', score: 85 },
    {
      id: 'eyes',
      pos: flipNot(eyeCenter),
      status: symmetryStatus,
      score: statusToScore(symmetryStatus),
    },
    {
      id: 'cheeks',
      pos: flipNot(cheekCenter),
      status: cheekStatus,
      score: statusToScore(cheekStatus),
    },
    {
      id: 'nose',
      pos: flipNot(noseTip),
      status: symmetryStatus,
      score: statusToScore(symmetryStatus),
    },
    {
      id: 'nasolabial',
      pos: flipNot(nlCenter),
      status: nlStatus,
      score: statusToScore(nlStatus),
    },
    {
      id: 'lips',
      pos: flipNot(lipCenter),
      status: lipStatus,
      score: statusToScore(lipStatus),
    },
    {
      id: 'chin',
      pos: flipNot(chin),
      status: jawStatus,
      score: statusToScore(jawStatus),
    },
    {
      id: 'jawline',
      pos: flipNot(jaw),
      status: jawStatus,
      score: statusToScore(jawStatus),
    },
  ]

  return {
    metrics,
    recommendations: recs.slice(0, 4),
    overallScore,
    subScores,
    zones,
  }
}

/**
 * Convert face-api 68-point landmark output (image-pixel coords) to normalized
 * 0-1 coords relative to source image dimensions.
 */
export function normalizeLandmarks(
  positions: { x: number; y: number }[],
  width: number,
  height: number,
): Point[] {
  if (!width || !height) return []
  return positions.map((p) => ({ x: p.x / width, y: p.y / height }))
}
